import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./api";
import { generateThumbnail } from "./thumbnail";

export interface ArtworkTemplateField {
  artworkTemplateFieldId: number; fieldName: string; maxCharacters: number | null; widthMm: number | null; sortOrder: number;
}
export interface ArtworkTemplateListRow {
  artworkTemplateId: number; datasheetId: number; companyName: string | null; datasheetTypeDescription: string | null;
  name: string; prepaidSize: number; prepaidSizeMultiple: number; fieldCount: number; hasImage: boolean; hasThumbnail: boolean;
}
export interface ArtworkTemplate {
  artworkTemplateId: number; datasheetId: number; companyName: string | null; datasheetTypeDescription: string | null;
  name: string; description: string | null; prepaidSize: number; prepaidSizeMultiple: number;
  heightMm: number | null; widthMm: number | null; hasImage: boolean;
  fields: ArtworkTemplateField[]; rowVersionBase64: string | null;
}
export interface ArtworkTemplateFieldInput { fieldName: string; maxCharacters: number | null; widthMm: number | null; }
export interface CreateArtworkTemplateBody {
  datasheetId: number; name: string; description: string | null; prepaidSize: number; prepaidSizeMultiple: number;
  heightMm: number | null; widthMm: number | null; fields: ArtworkTemplateFieldInput[];
}
export type UpdateArtworkTemplateBody = Omit<CreateArtworkTemplateBody, "datasheetId">;

const ifMatch = (rv: string | null) => (rv ? { "If-Match": rv } : ({} as Record<string, string>));

export const artworkTemplatesApi = {
  list: (datasheetId?: number) =>
    apiFetch<ArtworkTemplateListRow[]>(`/api/artwork-templates${datasheetId ? `?datasheetId=${datasheetId}` : ""}`),
  get: (id: number) => apiFetch<ArtworkTemplate>(`/api/artwork-templates/${id}`),
  create: (body: CreateArtworkTemplateBody) =>
    apiFetch<ArtworkTemplate>("/api/artwork-templates", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: UpdateArtworkTemplateBody, rv: string | null) =>
    apiFetch<ArtworkTemplate>(`/api/artwork-templates/${id}`, { method: "PATCH", body: JSON.stringify(body), headers: ifMatch(rv) }),
  remove: (id: number, rv: string | null) =>
    apiFetch<void>(`/api/artwork-templates/${id}`, { method: "DELETE", headers: ifMatch(rv) }),
  uploadImage: async (id: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    // Best-effort: null (unsupported type or any failure) simply means no thumbnail part.
    const thumb = await generateThumbnail(file);
    if (thumb) form.append("thumbnail", thumb, "thumbnail.webp");
    return apiFetch<void>(`/api/artwork-templates/${id}/image`, { method: "POST", body: form });
  },
  imageUrl: (id: number) => `/api/artwork-templates/${id}/image`,
  thumbnailUrl: (id: number) => `/api/artwork-templates/${id}/thumbnail`,
};

export const artworkTemplateKeys = {
  list: (datasheetId?: number) => ["artwork-templates", "list", datasheetId ?? null] as const,
  detail: (id: number) => ["artwork-templates", "detail", id] as const,
};

export const useArtworkTemplates = (datasheetId?: number) =>
  useQuery({ queryKey: artworkTemplateKeys.list(datasheetId), queryFn: () => artworkTemplatesApi.list(datasheetId) });

export const useArtworkTemplate = (id: number) =>
  useQuery({ queryKey: artworkTemplateKeys.detail(id), queryFn: () => artworkTemplatesApi.get(id), enabled: id > 0 });

const useInvalidate = () => {
  const qc = useQueryClient();
  return (id?: number) => {
    void qc.invalidateQueries({ queryKey: ["artwork-templates", "list"] });
    if (id) void qc.invalidateQueries({ queryKey: artworkTemplateKeys.detail(id) });
  };
};

export const useCreateArtworkTemplate = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (body: CreateArtworkTemplateBody) => artworkTemplatesApi.create(body), onSuccess: () => invalidate() });
};
export const useUpdateArtworkTemplate = (id: number) => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { body: UpdateArtworkTemplateBody; rowVersion: string | null }) => artworkTemplatesApi.update(id, v.body, v.rowVersion),
    onSuccess: () => invalidate(id),
  });
};
export const useDeleteArtworkTemplate = () => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (v: { id: number; rowVersion: string | null }) => artworkTemplatesApi.remove(v.id, v.rowVersion), onSuccess: () => invalidate() });
};
export const useUploadArtworkTemplateImage = (id: number) => {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (file: File) => artworkTemplatesApi.uploadImage(id, file), onSuccess: () => invalidate(id) });
};
